<template>
  <LISAPopover v-if="isLISA3" open-on-hover :disabled="disabled" class="p-0 border-0">
    <template #trigger="{ triggerAttrs }">
      <div v-bind="triggerAttrs">
        <slot name="activator" :trigger-attrs="triggerAttrs">
          <i v-bind="{ ...$attrs, ...$props }" :class="usedIcon"></i>
        </slot>
      </div>
    </template>
    <FormPanel :title="title || t('global.information')">
      <slot> {{ text }} </slot>
    </FormPanel>
  </LISAPopover>
  <div v-else class="inline-block info-tooltip">
    <div
      :style="{
        display: hidden ? 'none' : 'inline-block',
      }"
      :class="inlineBlockClass"
      :tabindex="disabled ? undefined : 0"
      role="button"
      :aria-label="ariaLabel ?? title ?? t('global.information')"
      @mouseover="showOverlay"
      @touchstart.passive="showOverlay"
      @click="showOverlay"
      @focusin="showOverlay"
      @focusout="resetTimer"
      @mouseleave="resetTimer"
    >
      <slot name="activator">
        <i
          v-bind="{ ...$attrs, ...$props }"
          :class="usedIcon"
          :style="`color: var(--p-${variant}-color);`"
        />
      </slot>
    </div>
    <Popover
      v-if="init"
      ref="op"
      :append-to="targetElement"
      :style="popoverStyle"
      :class="['popoverClass', { 'info-tooltip-popover--experimental': experimentalDesign }]"
      :pt="experimentalDesign ? undefined : { content: { class: 'p-0' } }"
    >
      <div v-if="experimentalDesign" class="font-bold pb-1" style="color: var(--p-primary-color)">
        <i :class="usedIcon" :style="`color: var(--p-${variant}-color);`" />
        {{ title ?? t("global.information") }}
      </div>
      <div v-else class="titleBackground">
        <h4 class="pt-2 pl-2 pr-2 mt-0">{{ title ?? t("global.information") }}</h4>
      </div>
      <slot v-if="rerenderContent === true ? visible : true" :op="op">
        <div :class="{ 'm-2': !experimentalDesign }">{{ text }}</div>
      </slot>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType, ref, onMounted, watch } from "vue";
import Popover from "primevue/popover";
import { LISAPopover } from "@fi-solutions/lisa-ui";
import FormPanel from "./form/FormPanel.vue";
import { isLISA3 } from "@/composables/useLISA3.ts";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  text: { type: String, required: false, default: "" },
  fullWidth: { type: Boolean, required: false, default: false },
  title: {
    type: String,
    required: false,
    default: null,
  },
  variant: {
    type: String as PropType<"error" | "warn" | "success" | "secondary" | "primary" | "info">,
    required: false,
    default: "",
  },
  size: {
    type: String as PropType<"fa-2xs" | "fa-xs" | "fa-sm" | "fa-lg" | "fa-xl" | "fa-2xl">,
    required: false,
    default: "fa-sm",
  },
  icon: {
    type: String,
    required: false,
    default: null,
  },
  target: {
    type: Function as PropType<() => HTMLElement | undefined>,
    default: null,
  },
  rerenderContent: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: String,
    default: "450px",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  inlineBlockClass: {
    type: String,
    default: undefined,
  },
  visibleTimeInMs: {
    type: Number,
    default: 700,
  },
  hidden: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
  experimentalDesign: {
    type: Boolean,
    default: false,
  },
});
const visible = ref(false);
const popoverStyle = computed(() => {
  return `max-width: ${props.maxWidth}${
    props.experimentalDesign ? "; border-radius: var(--p-border-radius-md)" : ""
  }`;
});
const usedIcon = computed(() => {
  if (props.icon != null) {
    return props.icon;
  }
  switch (props.variant) {
    case "error":
    case "warn":
      return `fas fa-exclamation-triangle ${props.size}`;
    default:
      return `fas fa-circle-info ${props.size}`;
  }
});
const op = ref<InstanceType<typeof Popover>>();
const timer = ref<number>();

watch(
  computed(() => props.disabled),
  () => {
    if (props.disabled) {
      hideOverlay();
    }
  },
);

function showOverlay(event: Event) {
  if (props.disabled) {
    return;
  }
  clearTimeout(timer.value);
  op.value?.show(event);
  visible.value = true;
}
function hideOverlay() {
  op.value?.hide();
  visible.value = false;
}
function resetTimer() {
  timer.value = setTimeout(() => {
    hideOverlay();
  }, props.visibleTimeInMs);
}
const targetElement = ref<HTMLElement>();
const init = ref(false);
onMounted(() => {
  if (props.target != null) {
    targetElement.value = props.target();
  }
  init.value = true;
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
h5 {
  margin-top: 0;
  margin-bottom: 0;
}
.titleBackground {
  background: var(--p-surface-100);
  height: 35px;
  border-bottom: 1px solid var(--p-surface-200);
  border-top-left-radius: var(--p-border-radius);
  border-top-right-radius: var(--p-border-radius);
}
</style>

<style lang="scss">
.info-tooltip * {
  pointer-events: all !important;
}
.popoverClass::after {
  border-bottom-color: var(--p-surface-100) !important;
}
.popoverClass.info-tooltip-popover--experimental::after,
.popoverClass.info-tooltip-popover--experimental::before {
  display: none !important;
}
</style>
